import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft,
  Upload,
  Check,
  Trash2,
  ChevronDown,
  FileText,
  AlertCircle,
  Info,
  Download,
  Settings,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import Button from '../../components/ui/Button';
import { useToast } from '../../contexts/ToastContext';
import { getAttributesRequest } from '../../store/attributes/attributes.action';
import { selectAttributes } from '../../store/attributes/attributes.selector';
import { IAttribute } from '../../types/attributes/attributes.types';
import {
  bulkCreateContactsRequest,
  clearContactState,
  selectBulkImportContacts,
} from '../../store/contacts';
import {
  selectContactsLoading,
  selectContactsError,
  selectContactsSuccess,
} from '../../store/contacts';
import { CreateContactRequest } from '../../types/contacts/contacts.types';
import { selectAccount } from 'store/accounts';

/** Split one CSV record line per RFC 4180 (quoted fields, `""` escapes). */
function parseCSVRecordLine(line: string): string[] {
  const fields: string[] = [];
  let field = '';
  let i = 0;
  let inQuotes = false;

  while (i < line.length) {
    const c = line[i];

    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += c;
      i += 1;
      continue;
    }

    if (c === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }

    if (c === ',') {
      fields.push(field.trim());
      field = '';
      i += 1;
      continue;
    }

    field += c;
    i += 1;
  }

  fields.push(field.trim());
  return fields;
}

/**
 * Split CSV text into physical rows; newlines inside double-quoted fields do not end a row.
 */
function splitCSVRows(text: string): string[] {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const rows: string[] = [];
  let row = '';
  let inQuotes = false;

  for (let i = 0; i < normalized.length; i++) {
    const c = normalized[i];

    if (inQuotes) {
      if (c === '"') {
        if (normalized[i + 1] === '"') {
          row += '""';
          i += 1;
        } else {
          inQuotes = false;
          row += '"';
        }
      } else {
        row += c;
      }
      continue;
    }

    if (c === '"') {
      inQuotes = true;
      row += '"';
      continue;
    }

    if (c === '\n') {
      rows.push(row);
      row = '';
      continue;
    }

    row += c;
  }

  if (row.length > 0) {
    rows.push(row);
  }

  return rows;
}

const UploadCSV: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const attributes = useSelector(selectAttributes);
  const contactsLoading = useSelector(selectContactsLoading);
  const contactsError = useSelector(selectContactsError);
  const contactsSuccess = useSelector(selectContactsSuccess);
  const bulkImportContacts = useSelector(selectBulkImportContacts);
  const account = useSelector(selectAccount);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string>('');
  const [identificationField, setIdentificationField] =
    useState<string>('email');
  const [addNewPeople, setAddNewPeople] = useState<boolean>(true);
  const [updateExistingPeople, setUpdateExistingPeople] =
    useState<boolean>(true);
  const [emptyValuesHandling, setEmptyValuesHandling] = useState<
    'ignore' | 'delete'
  >('ignore');
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
  const [csvData, setCsvData] = useState<{
    headers: string[];
    rows: string[][];
  } | null>(null);
  const [fieldMappings, setFieldMappings] = useState<Record<string, string>>(
    {}
  );
  const [selectedFields, setSelectedFields] = useState<Record<string, boolean>>(
    {}
  );

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch attributes on component mount
  useEffect(() => {
    dispatch(getAttributesRequest());
  }, [dispatch]);

  // Handle bulk create contacts success and error states
  useEffect(() => {
    if (contactsSuccess) {
      showToast({
        type: 'success',
        title: 'Import Complete',
        message: `Successfully imported contacts.`,
      });
      // Move to step 3 to show import summary
      setCurrentStep(3);
      dispatch(clearContactState());
    }
  }, [contactsSuccess, showToast, dispatch]);

  useEffect(() => {
    if (contactsError) {
      showToast({
        type: 'error',
        title: 'Import Failed',
        message: contactsError,
      });
      dispatch(clearContactState());
    }
  }, [contactsError, showToast, dispatch]);

  // Create dropdown options for field mapping
  const getFieldMappingOptions = (
    currentValue?: string
  ): Array<{ value: string; label: string; isStatic?: boolean }> => {
    const staticOptions = [
      { value: 'email', label: 'email', isStatic: true },
      { value: 'custom_id', label: 'custom_id', isStatic: true },
      { value: 'id', label: 'id', isStatic: true },
      { value: 'unsubscribed', label: 'unsubscribed', isStatic: true },
    ];

    const dynamicOptions =
      attributes?.attributes
        ?.filter((attr: IAttribute) => !attr.isDeleted)
        ?.map((attr: IAttribute) => ({
          value: attr.name,
          label: attr.label || attr.name,
          isStatic: false,
        })) || [];

    const allOptions = [...staticOptions, ...dynamicOptions];

    // If current value is not in the options, add it as a custom option
    if (
      currentValue &&
      !allOptions.find((option) => option.value === currentValue)
    ) {
      allOptions.push({
        value: currentValue,
        label: currentValue,
        isStatic: false,
      });
    }

    return allOptions;
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const parseCSV = (text: string): { headers: string[]; rows: string[][] } => {
    const lines = splitCSVRows(text).filter((line) => line.trim());
    if (lines.length === 0) return { headers: [], rows: [] };

    const headers = parseCSVRecordLine(lines[0]);
    const rows = lines.slice(1).map((line) => parseCSVRecordLine(line));
    return { headers, rows };
  };

  const handleFileSelect = (file: File) => {
    setError('');

    // Define valid file types and extensions
    const validTypes = [
      'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
      'application/vnd.ms-excel', // xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.template', // xltx
      'application/vnd.ms-excel.template.macroEnabled.12', // xltm
      'application/vnd.ms-excel.sheet.macroEnabled.12', // xlsm
      'application/vnd.ms-excel.sheet.binary.macroEnabled.12', // xlsb
      'application/vnd.oasis.opendocument.text', // odt
      'application/vnd.oasis.opendocument.spreadsheet', // ods
    ];

    const validExtensions = [
      '.csv',
      '.xlsx',
      '.xls',
      '.xltx',
      '.xltm',
      '.xlsm',
      '.xlsb',
      '.odt',
      '.ods',
    ];
    const hasValidExtension = validExtensions.some((ext) =>
      file.name.toLowerCase().endsWith(ext)
    );

    // Check if file is valid
    if (!validTypes.includes(file.type) && !hasValidExtension) {
      setError(
        'Please select a CSV or Excel file (.csv, .xlsx, .xls, .xltx, .xltm, .xlsm, .xlsb, .odt, .ods)'
      );
      return;
    }

    // Check file size (limit to 100MB)
    if (file.size > 100 * 1024 * 1024) {
      setError('File size must be less than 100MB');
      return;
    }

    setSelectedFile(file);

    // Parse file based on type
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const result = e.target?.result;

        if (
          file.type === 'text/csv' ||
          file.name.toLowerCase().endsWith('.csv')
        ) {
          // Handle CSV files
          const text = result as string;
          const parsed = parseCSV(text);
          setCsvData(parsed);

          // Initialize field mappings with default values
          const mappings: Record<string, string> = {};
          const selected: Record<string, boolean> = {};
          parsed.headers.forEach((header) => {
            mappings[header] = header;
            selected[header] = true; // All fields selected by default
          });
          setFieldMappings(mappings);
          setSelectedFields(selected);
        } else {
          // Handle Excel and other spreadsheet files
          if (typeof XLSX !== 'undefined') {
            const workbook = XLSX.read(result, { type: 'binary' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];

            // Convert to JSON with headers
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            if (jsonData.length > 0) {
              const headers = jsonData[0] as string[];
              const rows = jsonData
                .slice(1)
                .map((row: any) => headers.map((_, index) => row[index]));

              const parsed = { headers, rows };
              setCsvData(parsed);

              // Initialize field mappings with default values
              const mappings: Record<string, string> = {};
              const selected: Record<string, boolean> = {};
              headers.forEach((header) => {
                mappings[header] = header;
                selected[header] = true; // All fields selected by default
              });
              setFieldMappings(mappings);
              setSelectedFields(selected);
            }
          } else {
            setError(
              'Excel file parsing library not loaded. Please refresh the page and try again.'
            );
          }
        }
      } catch (error) {
        console.error('Error parsing file:', error);
        setError(
          'Unable to parse the uploaded file. Please check the file format.'
        );
      }
    };

    if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleNext = () => {
    if (currentStep === 1 && selectedFile) {
      setCurrentStep(2);
    }
  };

  const handleBack = (isCancel = false) => {
    if (currentStep === 1) {
      navigate('/contacts');
    } else if (currentStep === 2) {
      setCurrentStep(1);
    } else if (currentStep === 3) {
      navigate('/contacts');
    }
  };

  const backToContacts = () => {
    navigate('/contacts');
  };

  const handleDeleteFile = () => {
    setSelectedFile(null);
    setCsvData(null);
    setFieldMappings({});
    setSelectedFields({});
    setCurrentStep(1);
  };

  const handleFieldMappingChange = (
    csvHeader: string,
    attributeName: string
  ) => {
    setFieldMappings((prev) => ({
      ...prev,
      [csvHeader]: attributeName,
    }));
  };

  const handleFieldSelectionChange = (
    csvHeader: string,
    isSelected: boolean
  ) => {
    setSelectedFields((prev) => ({
      ...prev,
      [csvHeader]: isSelected,
    }));
  };

  const handleCompleteImport = () => {
    if (!csvData || !selectedFile) {
      showToast({
        type: 'error',
        title: 'No Data',
        message: 'Please upload a CSV file first.',
      });
      return;
    }

    // Show loading state
    showToast({
      type: 'info',
      title: 'Importing...',
      message: 'Your contacts are being imported. This may take a few moments.',
    });

    // Transform CSV data to contact objects for bulk creation
    const contactsToImport: CreateContactRequest[] = csvData.rows
      .map((row, index) => {
        const contact: CreateContactRequest = {
          email: '',
          firstName: '',
          lastName: '',
          custom_id: '',
          attributes: {},
          unsubscribed: false,
        };

        // Map CSV columns to contact fields based on field mappings
        csvData.headers.forEach((header, headerIndex) => {
          const value = row[headerIndex] !== undefined ? row[headerIndex] : '';
          const mappedField = fieldMappings[header];
          if (mappedField && selectedFields[header]) {
            if (mappedField === 'email') {
              contact.email = value;
            } else if (mappedField === 'firstName') {
              contact.firstName = value;
            } else if (mappedField === 'lastName') {
              contact.lastName = value;
            } else if (
              mappedField === 'custom_id' ||
              mappedField === 'customid' ||
              mappedField === 'customId'
            ) {
              contact.custom_id = value;
            } else if (mappedField === 'unsubscribed') {
              contact.unsubscribed =
                value.toString().toLowerCase() === 'true' ? true : false;
            } else {
              contact.attributes![mappedField] = value;
            }
          } else if (selectedFields[header]) {
            contact.attributes![header] = value;
          } else {
            contact.attributes![header] = value;
          }
        });
        return contact;
      })
      .filter((contact) => {
        // Filter out completely empty contacts
        return (
          contact.email ||
          contact.custom_id ||
          contact.firstName ||
          contact.lastName ||
          Object.keys(contact.attributes || {}).length > 0
        );
      });

    // Validate that we have contacts to import
    if (contactsToImport.length === 0) {
      showToast({
        type: 'error',
        title: 'No Valid Contacts',
        message:
          'No valid contacts found to import. Please check your field mappings.',
      });
      return;
    }

    // Dispatch bulk create contacts action
    dispatch(
      bulkCreateContactsRequest({
        contacts: contactsToImport,
        identifyBy: identificationField,
        emptyValuesHandling: emptyValuesHandling,
        addNewContacts: addNewPeople,
        updateExistingContacts: updateExistingPeople,
      })
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="mb-6">
        {/* Header */}
        <div className=" p-4 md:p-6">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="medium"
              onClick={backToContacts}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Contacts
            </Button>
            <div className="h-6 w-px bg-border" />
            <h1 className="text-xl font-semibold text-foreground">
              Import Contacts
            </h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Upload your CSV or Excel file to import contacts and map them to
            your contact attributes
          </p>
        </div>
        <div className="max-w-6xl mx-auto">
          {/* Step Progress Indicator */}
          <div className="mb-6">
            <div className="flex items-center justify-center gap-3 md:gap-6">
              {/* Step 1 */}
              <div className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                    currentStep >= 1
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'bg-muted'
                  }`}
                >
                  {currentStep > 1 ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Upload
                      className={`w-4 h-4 ${currentStep >= 1 ? 'text-primary-foreground' : 'text-muted-foreground'}`}
                    />
                  )}
                </div>
                <span
                  className={`mt-1 text-xs font-medium ${
                    currentStep >= 1
                      ? 'text-foreground'
                      : 'text-muted-foreground'
                  }`}
                >
                  Upload CSV
                </span>
              </div>

              <div
                className={`hidden md:block w-12 h-0.5 rounded-full transition-colors ${
                  currentStep >= 2 ? 'bg-primary' : 'bg-muted'
                }`}
              />

              {/* Step 2 */}
              <div className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                    currentStep >= 2
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'bg-muted'
                  }`}
                >
                  {currentStep > 2 ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Settings
                      className={`w-4 h-4 ${currentStep >= 2 ? 'text-primary-foreground' : 'text-muted-foreground'}`}
                    />
                  )}
                </div>
                <span
                  className={`mt-1 text-xs font-medium ${
                    currentStep >= 2
                      ? 'text-foreground'
                      : 'text-muted-foreground'
                  }`}
                >
                  Map Fields & Review
                </span>
              </div>

              <div
                className={`hidden md:block w-12 h-0.5 rounded-full transition-colors ${
                  currentStep >= 3 ? 'bg-primary' : 'bg-muted'
                }`}
              />

              {/* Step 3 */}
              <div className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                    currentStep >= 3
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'bg-muted'
                  }`}
                >
                  <Check
                    className={`w-4 h-4 ${currentStep >= 3 ? 'text-primary-foreground' : 'text-muted-foreground'}`}
                  />
                </div>
                <span
                  className={`mt-1 text-xs font-medium ${
                    currentStep >= 3
                      ? 'text-foreground'
                      : 'text-muted-foreground'
                  }`}
                >
                  Import Summary
                </span>
              </div>
            </div>
          </div>

          {/* Main Content */}
          {currentStep === 1 && (
            <div className="rounded-lg border border-border bg-card shadow-sm p-6 md:p-8">
              {/* File Upload Section */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="h-8 w-0.5 rounded-full bg-primary"
                      aria-hidden
                    />
                    <div>
                      <h3 className="text-sm font-semibold text-foreground tracking-tight">
                        Upload Your File
                      </h3>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        Upload a CSV or Excel file to import contacts into your
                        database
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="text-primary hover:text-primary-hover text-sm font-medium flex items-center gap-2 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-md"
                    onClick={(e) => {
                      e.stopPropagation();
                      showToast({
                        type: 'info',
                        title: 'Sample File',
                        message:
                          'Sample CSV file download will be available soon!',
                      });
                    }}
                  >
                    <Download className="w-4 h-4" />
                    <span className="hidden sm:inline">Download sample</span>
                  </button>
                </div>

                {/* File Upload Area or File Display */}
                {selectedFile ? (
                  <div className="mb-6">
                    <div className="border-2 border-success/40 rounded-lg p-4 bg-success-background">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center space-x-3 flex-1">
                          <div className="w-10 h-10 bg-success/20 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-success" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-foreground truncate">
                              {selectedFile.name}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {(selectedFile.size / 1024).toFixed(1)} KB • Ready
                              to import
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Check className="w-4 h-4 text-success" />
                            <span className="text-xs font-medium text-success hidden sm:inline">
                              Ready
                            </span>
                          </div>
                        </div>
                        <div className="flex space-x-2 w-full sm:w-auto">
                          <Button
                            variant="danger"
                            size="small"
                            onClick={handleDeleteFile}
                            className="flex items-center gap-1 text-sm flex-1 sm:flex-initial"
                          >
                            <Trash2 className="w-4 h-4" />
                            Remove
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`border-2 border-dashed rounded-lg p-8 md:p-12 text-center transition-all duration-200 cursor-pointer group focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 ${
                      isDragOver
                        ? 'border-primary bg-primary/10 scale-[1.01]'
                        : 'border-border hover:border-primary hover:bg-primary/5'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xls,.xltx,.xltm,.xlsm,.xlsb,.odt,.ods"
                      onChange={handleFileInputChange}
                      className="hidden"
                    />

                    <div className="space-y-6">
                      <div className="relative inline-block">
                        <div
                          className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center transition-all duration-200 ${
                            isDragOver
                              ? 'bg-primary/20 scale-105'
                              : 'bg-muted group-hover:bg-primary/15'
                          }`}
                        >
                          <Upload
                            className={`w-8 h-8 transition-colors ${
                              isDragOver
                                ? 'text-primary'
                                : 'text-muted-foreground group-hover:text-primary'
                            }`}
                          />
                        </div>
                        {isDragOver && (
                          <div className="absolute inset-0 w-16 h-16 mx-auto rounded-full bg-primary/30 animate-ping opacity-60" />
                        )}
                      </div>

                      <div className="space-y-2">
                        <div className="text-base font-semibold text-foreground">
                          {isDragOver
                            ? 'Drop your file here'
                            : 'Drag and drop your file here'}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          or{' '}
                          <span className="text-primary font-medium">
                            click to browse
                          </span>{' '}
                          your computer
                        </div>
                        <div className="text-xs text-muted-foreground pt-2">
                          Supports CSV, XLSX, XLS and other spreadsheet formats
                          • Max 100 MB
                        </div>
                      </div>

                      <div className="flex justify-center">
                        <Button
                          variant="primary"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            fileInputRef.current?.click();
                          }}
                          className="px-6 py-2"
                        >
                          Choose File
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-3 p-4 bg-destructive-background border border-border rounded-lg mb-6">
                  <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
                  <div className="text-sm text-destructive font-medium">
                    {error}
                  </div>
                </div>
              )}

              {/* Additional Configuration - Only show when file is uploaded */}
              {selectedFile && (
                <>
                  {/* Identification and Update Options */}
                  <div className="mb-6 pb-6 border-b border-border">
                    <div className="flex items-center gap-3 bg-muted/60 px-0 py-0 mb-4">
                      <div
                        className="h-8 w-0.5 rounded-full bg-primary"
                        aria-hidden
                      />
                      <h3 className="text-sm font-semibold text-foreground tracking-tight">
                        Identification & Update Options
                      </h3>
                    </div>
                    <div className="space-y-4">
                      <div className="bg-muted/40 rounded-lg p-4">
                        <label
                          htmlFor="identificationField"
                          className="block text-sm font-medium text-foreground mb-2"
                        >
                          How do you want to identify people?
                        </label>
                        <select
                          id="identificationField"
                          value={
                            account?.duplicateEmailEnabled
                              ? 'custom_id'
                              : identificationField
                          }
                          onChange={(e) =>
                            setIdentificationField(e.target.value)
                          }
                          disabled={account?.duplicateEmailEnabled}
                          className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-card transition-colors disabled:bg-muted disabled:cursor-not-allowed"
                        >
                          <option value="email">Email address</option>
                          <option value="custom_id">Custom ID</option>
                        </select>
                        <div className="mt-2 text-xs text-muted-foreground">
                          This field will be used to match existing contacts in
                          your database
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-4 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-foreground mb-1">
                              Add new people
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Create new contacts for people not found in your
                              database
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              if (addNewPeople && !updateExistingPeople) return;
                              setAddNewPeople(!addNewPeople);
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 shrink-0 ${
                              addNewPeople && !updateExistingPeople
                                ? 'bg-primary cursor-not-allowed opacity-75'
                                : addNewPeople
                                  ? 'bg-primary'
                                  : 'bg-muted'
                            }`}
                            disabled={addNewPeople && !updateExistingPeople}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-primary-foreground transition-transform shadow-sm ${
                                addNewPeople ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>

                        <div className="flex items-center justify-between p-4 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-foreground mb-1">
                              Update existing people
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Update information for contacts that already exist
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              if (updateExistingPeople && !addNewPeople) return;
                              setUpdateExistingPeople(!updateExistingPeople);
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 shrink-0 ${
                              updateExistingPeople && !addNewPeople
                                ? 'bg-primary cursor-not-allowed opacity-75'
                                : updateExistingPeople
                                  ? 'bg-primary'
                                  : 'bg-muted'
                            }`}
                            disabled={updateExistingPeople && !addNewPeople}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-primary-foreground transition-transform shadow-sm ${
                                updateExistingPeople
                                  ? 'translate-x-6'
                                  : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Empty Values Handling */}
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <Info className="w-4 h-4 text-primary shrink-0" />
                      <h3 className="text-sm font-semibold text-foreground tracking-tight">
                        What should we do with empty values?
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) =>
                          e.key === 'Enter' && setEmptyValuesHandling('ignore')
                        }
                        className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                          emptyValuesHandling === 'ignore'
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary/50 bg-card'
                        }`}
                        onClick={() => setEmptyValuesHandling('ignore')}
                      >
                        <div className="flex items-center space-x-3">
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                              emptyValuesHandling === 'ignore'
                                ? 'border-primary bg-primary'
                                : 'border-border'
                            }`}
                          >
                            {emptyValuesHandling === 'ignore' && (
                              <Check className="w-3 h-3 text-primary-foreground" />
                            )}
                          </div>
                          <div>
                            <span className="text-sm font-medium text-foreground cursor-pointer block">
                              Ignore empty values
                            </span>
                            <p className="text-xs text-muted-foreground mt-1">
                              Leave existing data unchanged
                            </p>
                          </div>
                        </div>
                      </div>

                      <div
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) =>
                          e.key === 'Enter' && setEmptyValuesHandling('delete')
                        }
                        className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                          emptyValuesHandling === 'delete'
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary/50 bg-card'
                        }`}
                        onClick={() => setEmptyValuesHandling('delete')}
                      >
                        <div className="flex items-center space-x-3">
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                              emptyValuesHandling === 'delete'
                                ? 'border-primary bg-primary'
                                : 'border-border'
                            }`}
                          >
                            {emptyValuesHandling === 'delete' && (
                              <Check className="w-3 h-3 text-primary-foreground" />
                            )}
                          </div>
                          <div>
                            <span className="text-sm font-medium text-foreground cursor-pointer block">
                              Delete existing attribute values
                            </span>
                            <p className="text-xs text-muted-foreground mt-1">
                              Clear existing data for empty fields
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 2: Map Fields */}
          {currentStep === 2 && csvData && (
            <div className="rounded-lg border border-border bg-card shadow-sm p-6 md:p-8">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  <div
                    className="h-8 w-0.5 rounded-full bg-primary"
                    aria-hidden
                  />
                  <div>
                    <h3 className="text-sm font-semibold text-foreground tracking-tight">
                      Map Fields
                    </h3>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Map your CSV columns to contact attributes
                    </p>
                  </div>
                </div>
                <Button
                  variant="danger"
                  size="small"
                  onClick={() => handleBack(true)}
                  className="flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Cancel
                </Button>
              </div>

              <div className="mb-6 p-4 bg-info-background border border-border rounded-lg">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-info mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-foreground mb-1">
                      Field Mapping Instructions
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Map your CSV columns to contact attributes. Uncheck
                      columns you don't want to import.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {csvData.headers.map((header, index) => {
                  const isSelected = selectedFields[header] ?? true;
                  const isRequired = false;
                  return (
                    <div
                      key={header}
                      className={`bg-card border-2 rounded-lg overflow-hidden transition-all duration-200 ${
                        isSelected
                          ? 'border-primary/50 shadow-sm'
                          : 'border-border opacity-60'
                      }`}
                    >
                      <div className="flex items-center justify-between p-3 border-b border-border bg-muted/60">
                        <div className="flex items-center gap-2">
                          <h4 className="text-xs font-semibold text-foreground font-mono">
                            {header}
                          </h4>
                          {isRequired && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-destructive-background text-destructive">
                              Required
                            </span>
                          )}
                        </div>
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) =>
                              handleFieldSelectionChange(
                                header,
                                e.target.checked
                              )
                            }
                            className="sr-only"
                            id={`checkbox-${header}`}
                          />
                          <label
                            htmlFor={`checkbox-${header}`}
                            className={`w-5 h-5 rounded border-2 cursor-pointer transition-all duration-200 flex items-center justify-center ${
                              isSelected
                                ? 'bg-primary border-primary'
                                : 'bg-card border-border hover:border-primary/50'
                            }`}
                          >
                            {isSelected && (
                              <Check className="w-3 h-3 text-primary-foreground" />
                            )}
                          </label>
                        </div>
                      </div>

                      <div className="p-3">
                        <div className="mb-3">
                          <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                            Sample Data
                          </h5>
                          <div className="space-y-1">
                            {csvData?.rows?.slice(0, 3).map((row, rowIndex) => (
                              <div
                                key={rowIndex}
                                className="flex items-center text-xs bg-muted/60 rounded p-1.5"
                              >
                                <div className="w-4 text-xs text-muted-foreground font-medium bg-card rounded px-1 text-center border border-border">
                                  {rowIndex + 1}
                                </div>
                                <div className="ml-2 text-foreground truncate flex-1 font-mono text-xs">
                                  {row[index] !== undefined
                                    ? row[index].toString()
                                    : '—'}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {!isSelected && (
                          <div className="text-center text-xs text-muted-foreground py-4 bg-muted/40 rounded-lg">
                            <div className="flex flex-col items-center gap-1">
                              <div className="w-6 h-6 bg-muted rounded-full flex items-center justify-center">
                                <Trash2 className="w-3 h-3 text-muted-foreground" />
                              </div>
                              <span>This column will be skipped</span>
                            </div>
                          </div>
                        )}

                        {isSelected && (
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                              Map to Attribute
                            </label>
                            <div className="relative">
                              <select
                                value={fieldMappings[header] || header}
                                onChange={(e) =>
                                  handleFieldMappingChange(
                                    header,
                                    e.target.value
                                  )
                                }
                                className="w-full px-3 py-2 text-xs border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary appearance-none bg-card pr-8 transition-colors"
                              >
                                {getFieldMappingOptions(
                                  fieldMappings[header] || header
                                ).map((option) => (
                                  <option
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
                            </div>

                            {isRequired && (
                              <div className="flex gap-1 mt-2">
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-primary/15 text-primary">
                                  Identifier
                                </span>
                                {header.toLowerCase() === 'email' && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-primary/15 text-primary">
                                    Identifier
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: Import Summary */}
          {currentStep === 3 && bulkImportContacts && (
            <div className="rounded-lg border border-border bg-card shadow-sm p-6 md:p-8">
              <div className="mb-6">
                <div className="flex items-center justify-center w-16 h-16 bg-success-background rounded-full mb-4 mx-auto">
                  <Check className="w-8 h-8 text-success" />
                </div>
                <h3 className="text-2xl font-bold text-foreground text-center mb-2">
                  Import Complete!
                </h3>
                <p className="text-sm text-muted-foreground text-center">
                  Your contacts have been processed successfully
                </p>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-success-background border-2 border-success/30 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium text-muted-foreground">
                      Added
                    </div>
                    <div className="w-10 h-10 bg-success/20 rounded-full flex items-center justify-center">
                      <Check className="w-5 h-5 text-success" />
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-foreground">
                    {bulkImportContacts.addedCount || 0}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    New contacts created
                  </div>
                </div>

                <div className="bg-info-background border-2 border-info/30 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium text-muted-foreground">
                      Updated
                    </div>
                    <div className="w-10 h-10 bg-info/20 rounded-full flex items-center justify-center">
                      <Settings className="w-5 h-5 text-info" />
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-foreground">
                    {bulkImportContacts.updatedCount || 0}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Existing contacts updated
                  </div>
                </div>

                <div
                  className={`border-2 rounded-lg p-6 ${
                    bulkImportContacts.errorsCount > 0
                      ? 'bg-destructive-background border-destructive/30'
                      : 'bg-muted/40 border-border'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium text-muted-foreground">
                      Errors
                    </div>
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        bulkImportContacts.errorsCount > 0
                          ? 'bg-destructive/20'
                          : 'bg-muted'
                      }`}
                    >
                      <AlertCircle
                        className={`w-5 h-5 ${
                          bulkImportContacts.errorsCount > 0
                            ? 'text-destructive'
                            : 'text-muted-foreground'
                        }`}
                      />
                    </div>
                  </div>
                  <div
                    className={`text-3xl font-bold ${
                      bulkImportContacts.errorsCount > 0
                        ? 'text-foreground'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {bulkImportContacts.errorsCount || 0}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Failed to import
                  </div>
                </div>
              </div>

              {/* Errors List */}
              {bulkImportContacts.errorsCount > 0 &&
                bulkImportContacts.errors &&
                bulkImportContacts.errors.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertCircle className="w-5 h-5 text-destructive" />
                      <h4 className="text-base font-semibold text-foreground">
                        Import Errors ({bulkImportContacts.errorsCount})
                      </h4>
                    </div>
                    <div className="bg-destructive-background border border-border rounded-lg p-4 max-h-64 overflow-y-auto">
                      <div className="space-y-2">
                        {bulkImportContacts.errors.map((error, index) => (
                          <div
                            key={index}
                            className="bg-card border border-border rounded-lg p-3 text-sm"
                          >
                            <div className="font-medium text-foreground mb-1">
                              {error.email ||
                                error.custom_id ||
                                `Contact ${index + 1}`}
                            </div>
                            {error.reason && (
                              <div className="text-destructive text-xs">
                                {error.reason}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

              {/* Total Summary */}
              <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-foreground">
                    Total Processed
                  </div>
                  <div className="text-lg font-bold text-foreground">
                    {(bulkImportContacts.addedCount || 0) +
                      (bulkImportContacts.updatedCount || 0) +
                      (bulkImportContacts.errorsCount || 0)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Bottom Navigation */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mt-8 pt-6 border-t border-border">
            {currentStep !== 3 && (
              <Button
                variant="outline"
                size="small"
                onClick={() => handleBack(false)}
                className="flex items-center gap-2 w-full md:w-auto justify-center"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
            )}
            {currentStep === 2 ? (
              <Button
                variant="success"
                size="small"
                onClick={handleCompleteImport}
                disabled={contactsLoading}
                className="flex items-center gap-2 px-6 py-2 w-full md:w-auto justify-center"
              >
                {contactsLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Complete Import
                  </>
                )}
              </Button>
            ) : currentStep === 3 ? (
              <div className="w-full flex justify-end">
                <Button
                  variant="primary"
                  size="small"
                  onClick={() => navigate('/contacts')}
                  className="flex items-center gap-2 px-6 py-2 w-full md:w-auto justify-center"
                >
                  View Contacts
                  <ArrowLeft className="w-4 h-4 rotate-180" />
                </Button>
              </div>
            ) : (
              <Button
                variant="primary"
                size="small"
                onClick={handleNext}
                disabled={currentStep === 1 && !selectedFile}
                className="flex items-center gap-2 px-6 py-2 w-full md:w-auto justify-center"
              >
                Next Step
                <ArrowLeft className="w-4 h-4 rotate-180" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadCSV;
